import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { eventBus } from '../core/EventBus';

// 链路劣化倒计时提示卡片：
//   start     → 质量差开窗，显示"检测到链接质量差，N秒后切换到X"+ 倒计时进度条（进度条走的就是劣化确认窗口）
//   recovered → 窗口内质量回落，"链接质量已恢复，已取消切换"短暂展示后撤下
//   switching → 窗口熬满已发起切换，"正在切换到X…"短暂展示后撤下
//   dismiss   → 被防护拦下/断连，立即撤下
// 由 PathObserver 通过 eventBus('path:degrade') 驱动，自管状态。

interface NoticeState {
  phase: 'start' | 'recovered' | 'switching';
  label: string;
  windowMs: number;
  startTs: number;
}

const DegradeCountdown: React.FC = () => {
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [progress, setProgress] = useState(0); // 0~1 倒计时进度
  const fadeTimer = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  // 监听 path:degrade 事件
  useEffect(() => {
    const clearFade = () => {
      if (fadeTimer.current !== null) { clearTimeout(fadeTimer.current); fadeTimer.current = null; }
    };
    const unsub = eventBus.on('path:degrade', (d) => {
      clearFade();
      if (d.phase === 'start') {
        setProgress(0);
        setNotice({ phase: 'start', label: d.label ?? '', windowMs: d.windowMs ?? 5000, startTs: d.startTs ?? Date.now() });
      } else if (d.phase === 'recovered') {
        setNotice((prev) => (prev ? { ...prev, phase: 'recovered' } : null));
        fadeTimer.current = window.setTimeout(() => setNotice(null), 1800);
      } else if (d.phase === 'switching') {
        setNotice((prev) => (prev ? { ...prev, phase: 'switching', label: d.label ?? prev.label } : null));
        fadeTimer.current = window.setTimeout(() => setNotice(null), 2500);
      } else {
        setNotice(null); // dismiss
      }
    });
    return () => { unsub(); clearFade(); };
  }, []);

  // 倒计时进度条动画（仅 start 态用 rAF 平滑推进，不依赖 stats tick 频率）
  useEffect(() => {
    if (!notice || notice.phase !== 'start') {
      if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      return;
    }
    const tick = () => {
      const p = Math.min(1, (Date.now() - notice.startTs) / notice.windowMs);
      setProgress(p);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; } };
  }, [notice]);

  if (!notice) return null;

  const remainSec = Math.max(0, Math.ceil((notice.windowMs * (1 - progress)) / 1000));
  const isRecovered = notice.phase === 'recovered';
  const isSwitching = notice.phase === 'switching';

  return (
    <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[210] pointer-events-none">
      <div className="glass-high px-4 py-3 rounded-xl shadow-2xl border border-white/10 min-w-[280px] max-w-[360px] animate-slide-down">
        <div className="flex items-center gap-2.5">
          {isRecovered ? (
            <CheckCircle size={18} className="text-green-500 shrink-0" />
          ) : isSwitching ? (
            <RefreshCw size={18} className="text-blue-400 shrink-0 animate-spin" />
          ) : (
            <AlertTriangle size={18} className="text-amber-500 shrink-0" />
          )}
          <span className="text-sm font-medium text-white">
            {isRecovered
              ? '链接质量已恢复，已取消切换'
              : isSwitching
                ? `正在切换到${notice.label}…`
                : `检测到链接质量差，${remainSec}秒后切换到${notice.label}`}
          </span>
        </div>
        {!isRecovered && !isSwitching && (
          <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full bg-amber-500 transition-[width] duration-100 ease-linear"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DegradeCountdown;
